/**
 * Converts CSV content into a JSON array.
 *
 * @param {string} csvContent - The content of the CSV file to be converted.
 * @param {Object} options - Conversion options.
 * @param {boolean} [options.header=true] - Specifies if the CSV has a header row.
 * @param {string} [options.separator=','] - The delimiter character used in the CSV.
 * @returns {Promise<Array<Object>>} - A promise that resolves with the converted JSON array.
 * @throws {string} - If the CSV content is empty.
 *
 * @example
 *
 * csvToJson("name,firstname\nJohn,Doe\nAlice,Bob", { header: true })
 * // Returns [{ name: "John", firstname: "Doe" }, { name: "Alice", firstname: "Bob" }]
 *
 */
export function csvToJson(
  csvContent,
  options = { header: true, separator: ';' }
) {
  return new Promise((resolve, reject) => {
    const lines = csvContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)

    if (lines.length === 0) {
      return reject(new Error('Empty csv file'))
    }

    let headers = lines[0].split(options.separator)

    if (!options.header) {
      headers = Array.from({ length: headers.length }).map((_, i) => `col${i}`)
    }

    const jsonData = lines.slice(options.header ? 1 : 0).map((line) => {
      options.onLineRead && options.onLineRead(line)
      const values = line.split(options.separator)
      let obj = {}

      headers.forEach((header, index) => {
        obj[header] = values[index]
      })

      return obj
    })

    return resolve(jsonData)
  })
}

/**
 * Reads a CSV File and returns its content as a JSON array.
 *
 * @param {File} csvFile - The CSV File object to be read and converted.
 * @param {Object} options - Conversion options.
 * @param {boolean} [options.header=true] - Specifies if the CSV has a header row.
 * @param {string} [options.separator=','] - The delimiter character used in the CSV.
 * @returns {Promise<Array<Object>>} - A promise that resolves with the converted JSON array.
 *
 * @example
 *
 * const file = event.target.files[0];
 * CSVFileToJson(file, { header: true })
 * .then(data => {
 *   console.log(data); // [{ name: "John", firstname: "Doe" }, { name: "Alice", firstname: "Bob" }]
 * })
 *
 */
export function CSVFileToJson(
  csvFile,
  options = { header: true, separator: ';' }
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const jsonData = csvToJson(event.target.result, options)

        resolve(jsonData)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read the file'))
    }

    reader.readAsText(csvFile)
  })
}
